import React from 'react';
import { Alert, ProgressCircle, ToggleSwitch } from '@americanexpress/dls-react';

import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

import { DLSSize } from 'src/types/dls-size.enum';
import styles from './toggleAccountRow.module.scss';
import { ToggleAccountRowViewModel } from 'src/types/view-models/listAccounts/accountsList.view.model';
import layout from 'src/styles/layout.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import { AccountSyncResult } from 'src/types/account-sync-status.model';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import { useHideAccount, useResyncAccount, useShowAccount } from 'src/hooks/queries/accounts';
import PlaidReconnectInstitution from 'src/components/atomic/atoms/plaidConnector/PlaidReconnectInstitution';
import Selectors from 'src/utils/selectors';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';

export default function ToggleAccountRow({
    title,
    subtitle,
    id,
    balance,
    hidden = false,
    className,
    onClick,
    showBalance = true,
    isSyncing = false,
    error = null,
    institution,
    accountId
}: ToggleAccountRowViewModel): JSX.Element {
    let allClassNames = styles.accountRow;
    if (className) allClassNames += ` ${className}`;
    const titleStyle: string = hidden && !isSyncing && `${styles.hiddenStyle}`;
    const subtitleStyle: string = hidden && !isSyncing ? styles.hiddenStyle : styles.accountRowSubtitle;
    const { name, accountNumber } = splitAccountString(title);
    const ariaLabel: string =
        accountNumber && accountNumber.length > 0
            ? LiteralsService.getLiteral('Budget.Settings.toggleNameNameAriaLabel', [name, accountNumber])
            : LiteralsService.getLiteral('Budget.Settings.toggleNameNameAriaLabelNoAccount', [name]);
    const idForAriaLabel: string = ariaLabel.replace(/\s/g, '');
    const { mutateAsync: resyncAccount } = useResyncAccount();
    const { mutateAsync: showAccount, isLoading: isShowingAccount } = useShowAccount();
    const { mutateAsync: hideAccount, isLoading: isHidingAccount } = useHideAccount();

    const handleToggleClick = async (): Promise<void> => {
        if (onClick) {
            await onClick();
        }
        if (hidden) {
            await showAccount(accountId);
            trackClick(location.pathname, adobeTags.AccountConnection.AccountOn);
        } else {
            await hideAccount(id);
            trackClick(location.pathname, adobeTags.AccountConnection.AccountOff);
        }
    };

    const isLoading = isSyncing || isShowingAccount || isHidingAccount;

    function splitAccountString(accountName: string): { name: string; accountNumber: string } {
        const name = accountName.replace(/\s\(-\d+\)/, '');
        let accountNumber = '';
        const digitMatch = accountName.match(/\(([^)]+)\)/);
        if (digitMatch?.[1]) {
            const digits = digitMatch[1].match(/\d/g);
            if (digits) {
                accountNumber = digits.join('').split('').join(' ');
            }
        }
        return { name, accountNumber };
    }

    return (
        <div className={allClassNames} data-cy={Selectors.SpendSmart.ACCOUNT_ROW}>
            <div>
                <Typography
                    data-cy={Selectors.SpendSmart.ACCOUNT_ROW_TITLE}
                    component="p"
                    variant={TypographyVariants.SUBTEXT_BOLD}
                    className={titleStyle}
                    aria-hidden={true}
                >
                    {title}
                </Typography>
                <span className={styles.visuallyHidden}>
                    {accountNumber.length > 0
                        ? LiteralsService.getLiteral('Budget.Settings.accountNameAriaLabel', [name, accountNumber])
                        : LiteralsService.getLiteral('Budget.Settings.accountNameAriaLabel2', [name])}
                </span>
                <Typography
                    data-cy={Selectors.SpendSmart.ACCOUNT_ROW_SUBTITLE}
                    component="p"
                    variant={TypographyVariants.CAPTION}
                    className={subtitleStyle}
                >
                    {subtitle}
                </Typography>
                {error && (
                    <Alert data-cy={Selectors.General.INLINE_ERROR}>
                        {error === AccountSyncResult.ERROR_RESYNC_NEEDED && (
                            <>
                                {LiteralsService.getLiteral('InlineSyncError.unableToUpdate')}
                                <Anchor
                                    data-cy={Selectors.General.TRY_AGAIN_LINK}
                                    onClick={() => {
                                        if (id) void resyncAccount(id);
                                    }}
                                    tag={adobeTags.Error.UnableToUpdate}
                                >
                                    {LiteralsService.getLiteral('InlineSyncError.tryAgain')}
                                </Anchor>
                            </>
                        )}
                        {(error === AccountSyncResult.ERROR_PERMANENT ||
                            error === AccountSyncResult.ERROR_PHANT_INTERNAL) && (
                            <>{LiteralsService.getLiteral('InlineSyncError.unableToLink')}</>
                        )}
                        {error === AccountSyncResult.ERROR_PLAID_RELINK_NEEDED && (
                            <PlaidReconnectInstitution className={styles.reconnectButton} id={institution.id}>
                                {LiteralsService.getLiteral('InlineSyncError.reconnect')}
                            </PlaidReconnectInstitution>
                        )}
                    </Alert>
                )}
            </div>
            {showBalance && (
                <Typography
                    data-cy={Selectors.SpendSmart.ACCOUNT_ROW_BALANCE}
                    component="p"
                    variant={TypographyVariants.BODY}
                >
                    {balance}
                </Typography>
            )}

            <div aria-live="polite" aria-busy={isLoading}>
                {isLoading ? (
                    <ProgressCircle data-cy={Selectors.General.LOADING_INDICATOR} size={DLSSize.SMALL} />
                ) : (
                    <ToggleSwitch
                        className={styles.toggleSwitch}
                        onClick={handleToggleClick}
                        checked={!hidden}
                        aria-labelledby={idForAriaLabel}
                        data-cy={Selectors.SpendSmart.TOGGLE_ACCOUNTS_BUTTON}
                    />
                )}
                <span id={idForAriaLabel} className={layout.screenReaderOnly}>
                    {ariaLabel}
                </span>
            </div>
        </div>
    );
}
